import React, { useState, useEffect } from "react";
import { Modal, Button, Form, FloatingLabel } from "react-bootstrap";
import styles from "./stylesAdmin.module.css"
import { Funcionario, Organica, Establecimiento, ReparticionGde, SectorGde } from "../../types/interfaces";
import { useLoading } from "../../components/LoadingContext";

interface EditFuncionarioModalProps {
  show: boolean;
  onHide: () => void;
  onEditItem?: (item: Funcionario) => void;
  item: Funcionario;
  showNotification: (message: string, variant: "success" | "danger") => void;
}

export default function EditFuncionarioModal({
  show,
  onHide,
  onEditItem,
  item,
  showNotification,
}: EditFuncionarioModalProps) {
  const { setLoading } = useLoading();
  const [formData, setFormData] = useState<any>({
    funcionario: "",
    organica: {},
    establecimiento: {},
    ubi_adicional: null,
    interno: null,
    correo: "",
    revista: "",
    reparticion_gde: {},
    sector_gde: {},
    usuario_gde: ""
  });
  const [organicas, setOrganicas] = useState<Organica[]>([]);
  const [establecimientos, setEstablecimientos] = useState<Establecimiento[]>([]);
  const [reparticionesGde, setReparticionesGde] = useState<ReparticionGde[]>([]);
  const [sectoresGde, setSectoresGde] = useState<SectorGde[]>([]);
  const [filteredSectores, setFilteredSectores] = useState<SectorGde[]>([]);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [organicasResponse, establecimientosResponse, reparticionesGdeResponse, sectoresGdeResponse, funcionarioEditResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/admin/allOrganicas`, { method: "GET", headers: {"Content-Type": "application/json",}, credentials: "include" }),
          fetch(`${process.env.REACT_APP_API_URL}/api/admin/allEstablecimientos`, { method: "GET", headers: {"Content-Type": "application/json",}, credentials: "include" }),
          fetch(`${process.env.REACT_APP_API_URL}/api/admin/allReparticionesGde`, { method: "GET", headers: {"Content-Type": "application/json",}, credentials: "include" }),
          fetch(`${process.env.REACT_APP_API_URL}/api/admin/allSectoresGde`, { method: "GET", headers: {"Content-Type": "application/json",}, credentials: "include" }),
          fetch(`${process.env.REACT_APP_API_URL}/api/admin/editFuncionario/${item.id}`, { method: "GET", headers: {"Content-Type": "application/json",}, credentials: "include" })
        ]);

        if (!organicasResponse.ok || !establecimientosResponse.ok || !reparticionesGdeResponse.ok || !sectoresGdeResponse.ok || !funcionarioEditResponse.ok) {
          throw new Error("Network response was not ok");
        }

        const [organicasData, establecimientosData, reparticionesGdeData, sectoresGdeData, funcionarioEditData] = await Promise.all([
          organicasResponse.json(),
          establecimientosResponse.json(),
          reparticionesGdeResponse.json(),
          sectoresGdeResponse.json(),
          funcionarioEditResponse.json(),
        ]);

        setOrganicas(organicasData);
        setEstablecimientos(establecimientosData);
        setReparticionesGde(reparticionesGdeData);
        setSectoresGde(sectoresGdeData);

        setFormData({
          funcionario: funcionarioEditData.funcionario,
          organica: funcionarioEditData.organica || {},
          establecimiento: funcionarioEditData.establecimiento || {},
          ubi_adicional: funcionarioEditData.ubi_adicional || null,
          interno: funcionarioEditData.interno || null,
          correo: funcionarioEditData.correo,
          revista: funcionarioEditData.revista,
          reparticion_gde: funcionarioEditData.reparticion_gde || {},
          sector_gde: funcionarioEditData.sector_gde || {},
          usuario_gde: funcionarioEditData.usuario_gde
        });

      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

  if (item?.id) {
    fetchData();
    }
  }, [item, setLoading]);

  useEffect(() => {
    if (formData.reparticion_gde && formData.reparticion_gde.id) {
      const sectorsForReparticion = sectoresGde.filter(
        (sector) => sector.reparticion_id === formData.reparticion_gde.id
      );
      setFilteredSectores(sectorsForReparticion);
    } else {
      setFilteredSectores(sectoresGde);
    }
  }, [sectoresGde, formData.reparticion_gde]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    const findIdByName = (name: string, list: any[]) => {
      const selectedItem = list.find((item) => item.nombre === name);
      return selectedItem ? selectedItem.id : null;
    };

    setFormData((prevData: any) => {
      switch (name) {
        case "organica.nombre":
          const organicaId = findIdByName(value, organicas);
          return { ...prevData, organica: { nombre: value, id: organicaId } };
        case "establecimiento.nombre":
          const establecimientoId = findIdByName(value, establecimientos);
          return {
            ...prevData,
            establecimiento: { nombre: value, id: establecimientoId },
          };
        case "reparticion_gde.nombre":
          const reparticionId = findIdByName(value, reparticionesGde);
          return {
            ...prevData,
            reparticion_gde: { nombre: value, id: reparticionId },
          };
        case "sector_gde.nombre":
          const sectorId = findIdByName(value, sectoresGde);
          return { ...prevData, sector_gde: { nombre: value, id: sectorId } };
        default:
          return prevData;
      }
    });
  }

  const handleReparticionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    const selectedReparticion = reparticionesGde.find(
      (r) => r.nombre === value
    );

    setFormData((prevData: any) => ({
      ...prevData,
      reparticion_gde: { nombre: value, id: selectedReparticion?.id },
    }));

    const sectorsForReparticion = sectoresGde.filter(
      (sector) => sector.reparticion_id === selectedReparticion?.id
    );
    setFilteredSectores(sectorsForReparticion);
  };

  const validateForm = () => {
    const newErrors: any = {};
    let hasErrors = false;

    if (!formData.funcionario) {
      newErrors.funcionario = "Este campo es obligatorio";
      hasErrors = true;
    } else if (formData.funcionario.length < 5) {
      newErrors.funcionario = "Este campo debe tener al menos 5 caracteres";
      hasErrors = true;
    }
    if (!formData.organica || !formData.organica.nombre) {
      newErrors.organica = "Este campo es obligatorio";
      hasErrors = true;
    }
    if (!formData.establecimiento || !formData.establecimiento.nombre) {
      newErrors.establecimiento = "Este campo es obligatorio";
      hasErrors = true;
    }
    // if (!formData.ubi_adicional) {
    //   newErrors.ubi_adicional = "Este campo es obligatorio";
    //   hasErrors = true;
    // }
    // if (!formData.interno) {
    //   newErrors.interno = "Este campo es obligatorio";
    //   hasErrors = true;
    // } else if (isNaN(Number(formData.interno))) {
    //   newErrors.interno = "Este campo debe ser un número";
    //   hasErrors = true;
    // }
    // } 
    if (!formData.correo) {
      newErrors.correo = "Este campo es obligatorio";
      hasErrors = true;
    } else if (!validateEmail(formData.correo)) {
      newErrors.correo = "Debe tener un formato válido (@iosfa.gob.ar)";
      hasErrors = true;
    }
    if (!formData.usuario_gde) {
      newErrors.usuario_gde = "Este campo es obligatorio";
      hasErrors = true;
    } else if (formData.usuario_gde.length < 3) {
      newErrors.usuario_gde = "Este campo debe tener al menos 3 caracteres";
      hasErrors = true;
    }
    if (!formData.reparticion_gde || !formData.reparticion_gde.nombre) {
      newErrors.reparticion_gde = "Este campo es obligatorio";
      hasErrors = true;
    }
    if (!formData.sector_gde || !formData.sector_gde.nombre) {
      newErrors.sector_gde = "Este campo es obligatorio";
      hasErrors = true;
    }
    if (!formData.revista) {
      newErrors.revista = "Este campo es obligatorio";
      hasErrors = true;
    } else if (formData.revista.length < 8) {
      newErrors.revista = "Este campo debe tener al menos 8 caracteres";
      hasErrors = true;
    }

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setLoading(true);
    e.preventDefault();
  
    if (!validateForm()) {
      return;
    }

    if (formData && onEditItem) {
    const transformedData = {
      funcionario: formData.funcionario,
      organica_id: formData.organica?.id || null,
      establecimiento_id: formData.establecimiento?.id || null,
      ubi_adicional: formData.ubi_adicional || null,
      interno: formData.interno || null,
      correo: formData.correo,
      revista: formData.revista,
      reparticion_id: formData.reparticion_gde?.id || null,
      sector_id: formData.sector_gde?.id || null,
      usuario_gde: formData.usuario_gde,
    };
  
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/editFuncionario/${item.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transformedData),
        credentials: "include"
      });
  
      if (!response.ok) {
        throw new Error('Failed to edit funcionario');
      }

      const funcionarioDetailsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/funcionario/${item.id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!funcionarioDetailsResponse.ok) {
        throw new Error('Failed to fetch updated funcionario details');
      }
      const funcionarioDetails = await funcionarioDetailsResponse.json();
      showNotification("Funcionario/a editado/a con éxito", "success");
      setFormData({});
      onHide(); 

      onEditItem(funcionarioDetails);
    } catch (error) {
      showNotification("Error al editar funcionario/a", "danger");
      console.error('Error editing funcionario:', error);
    } finally {
      setLoading(false);
    }
  }
};

const validateEmail = (email: string): boolean => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

  return (
    <Modal show={show} onHide={onHide} className={styles.modalContainer}>
              <Form onSubmit={handleSubmit}>
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>Editar Funcionario/a</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
          <FloatingLabel controlId="floatingInputFuncionario" label="Nombre(s) y apellido(s)" className="mb-3">
            <Form.Control type="text" placeholder="Nombre(s) y apellido(s)" name="funcionario" onChange={handleInputChange} value={formData.funcionario || ""} />
          </FloatingLabel>
          {errors.funcionario && <div className={styles.errors}>{errors.funcionario}</div>}
          <FloatingLabel controlId="floatingSelectOrganica" label="Orgánica" className="mb-3">
            <Form.Select aria-label="Orgánica" name="organica.nombre" onChange={handleSelectChange} value={formData.organica?.nombre || ""}>
              <option value="" disabled>Seleccionar orgánica</option>
              {organicas.sort((a, b) => a.nombre.localeCompare(b.nombre)).map((organica) => (
                <option key={organica.id} value={organica.nombre}>{organica.nombre}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
          {errors.organica && <div className={styles.errors}>{errors.organica}</div>}
          <FloatingLabel controlId="floatingSelectEstablecimiento" label="Establecimiento" className="mb-3">
            <Form.Select aria-label="Establecimiento" name="establecimiento.nombre" onChange={handleSelectChange} value={formData.establecimiento?.nombre || ""}>
              <option value="" disabled>Seleccionar establecimiento</option>
              {establecimientos.sort((a, b) => a.nombre.localeCompare(b.nombre)).map((establecimiento) => (
                <option key={establecimiento.id} value={establecimiento.nombre}>{establecimiento.nombre}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
          {errors.establecimiento && <div className={styles.errors}>{errors.establecimiento}</div>}
          <FloatingLabel controlId="floatingInputUbiAdicional" label="Ubicación adicional" className="mb-3">
            <Form.Control type="text" placeholder="Ubicación adicional" name="ubi_adicional" onChange={handleInputChange} value={formData.ubi_adicional || ""} />
          </FloatingLabel>
          {/* {errors.ubi_adicional && <div className={styles.errors}>{errors.ubi_adicional}</div>} */}
          <FloatingLabel controlId="floatingInputInterno" label="Interno" className="mb-3">
            <Form.Control type="text" placeholder="Interno" name="interno" onChange={handleInputChange} value={formData.interno || ""} />
          </FloatingLabel>
          {/* {errors.interno && <div className={styles.errors}>{errors.interno}</div>} */}
          <FloatingLabel controlId="floatingInputCorreo" label="Correo electrónico" className="mb-3">
            <Form.Control type="text" placeholder="Correo electrónico" name="correo" onChange={handleInputChange} value={formData.correo || ""} />
          </FloatingLabel>
          {errors.correo && <div className={styles.errors}>{errors.correo}</div>}
          <FloatingLabel controlId="floatingInputUsuarioGde" label="Usuario GDE" className="mb-3">
            <Form.Control type="text" placeholder="Usuario GDE" name="usuario_gde" onChange={handleInputChange} value={formData.usuario_gde || ""} />
          </FloatingLabel>
          {errors.usuario_gde && <div className={styles.errors}>{errors.usuario_gde}</div>}
          <FloatingLabel controlId="floatingSelectReparticionGde" label="Repartición GDE" className="mb-3">
            <Form.Select aria-label="Repartición GDE" name="reparticion_gde.nombre" onChange={handleReparticionChange} value={formData.reparticion_gde?.nombre || ""}>
              <option value="" disabled>Seleccionar repartición</option>
              {reparticionesGde.sort((a, b) => a.nombre.localeCompare(b.nombre)).map((reparticion) => (
                <option key={reparticion.id} value={reparticion.nombre}>{reparticion.nombre}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
          {errors.reparticion_gde && <div className={styles.errors}>{errors.reparticion_gde}</div>}
          <FloatingLabel controlId="floatingSelectSectorGde" label="Sector GDE" className="mb-3">
            <Form.Select aria-label="Sector GDE" name="sector_gde.nombre" onChange={handleSelectChange} value={formData.sector_gde?.nombre || ""}>
              <option value="">Seleccionar sector</option>
              {filteredSectores.sort((a, b) => a.nombre.localeCompare(b.nombre)).map((sector) => (
                <option key={sector.id} value={sector.nombre}>{sector.nombre}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
          {errors.sector_gde && <div className={styles.errors}>{errors.sector_gde}</div>}
          <FloatingLabel controlId="floatingInputRevista" label="Revista" className="mb-3">
            <Form.Control type="text" placeholder="Revista" name="revista" onChange={handleInputChange} value={formData.revista || ""} />
          </FloatingLabel>
          {errors.revista && <div className={styles.errors}>{errors.revista}</div>}
          </Modal.Body>
          <Modal.Footer className={styles.modalFooter}>
          <Button variant="warning" type="submit">Actualizar</Button>
          </Modal.Footer>
        </Form>
    </Modal>
  );
}
